import React from "react";
import { useI18n } from "i18n";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { FormControl } from "@remo-co/ui-core/src/components/FormControl";
import { Input } from "@remo-co/ui-core/src/components/Input";
import { isValidEmailAddress } from "helpers/emailHelper";
import { useResetPassword } from "../../hooks/resetPassword";
import { styles } from "./styles";

interface ForgetPasswordFormProps {
  email: string;
  continueURL?: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
}

export const ForgetPasswordForm = ({
  email,
  continueURL,
  onChange,
}: ForgetPasswordFormProps): JSX.Element => {
  const [touched, setTouched] = React.useState(false);
  const { t } = useI18n(["auth"]);
  const resetPassword = useResetPassword();
  const [sendingResetInstructions, setSendingResetInstructions] =
    React.useState<boolean>(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setTouched(true);

    setSendingResetInstructions(true);
    await resetPassword.sendPasswordResetEmail(email, continueURL);
    setSendingResetInstructions(false);
  };

  return (
    <form noValidate onSubmit={onSubmit}>
      <Container top="md" bottom="md">
        <FormControl sx={styles.formControl}>
          <Input
            label={t("auth:your.email")}
            id="email"
            fullWidth
            required
            autoFocus
            onChange={onChange}
            onBlur={() => {
              setTouched(true);
            }}
            size="sm"
            value={email}
            error={
              touched && !isValidEmailAddress(email)
                ? t("auth:enter.valid.email").toString()
                : ""
            }
          />
        </FormControl>
      </Container>
      <Button
        variant="primary"
        color="blue"
        fullWidth
        size="lg"
        disabled={sendingResetInstructions || email === ""}
        data-testid="forgot-submit-button"
        type="submit"
      >
        {t("auth:send.reset.instructions")}
      </Button>
    </form>
  );
};
